// The guided tour of Leave & Holidays, one of the four My Info pages.
//
// Steps point at [data-tour] anchors on people/MyInfoPage.jsx. Several of the
// panels only render with data behind them (the extra allowance boxes, the
// days-off list, the breakdown table), and the engine shows those steps
// centred when the element is missing, so each body reads standalone as well
// as pointed. The request form is a modal: the steps inside it open it and the
// steps either side close it, so stepping backwards leaves the screen as
// stepping forwards did.

export const MY_LEAVE_TOUR_ID = 'my-leave';

const openRequestModal = () => {
  if (document.querySelector('[data-tour="leave-request-modal"]')) return;
  document.querySelector('[data-tour="leave-request"]')?.click();
};
const closeRequestModal = () => {
  document.querySelector('[data-tour="leave-request-modal-close"]')?.click();
};

export const myLeaveTourSteps = [
  {
    title: 'Welcome to My Leave & Holidays',
    before: closeRequestModal,
    body: [
      'This page is your leave in one place: how much you have, what is already booked, the days you get without booking anything, and where a request goes once it is made.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. It will not pop up again either way.',
    ],
  },
  {
    title: 'Your allowance for the year',
    target: '[data-tour="leave-summary"]',
    before: closeRequestModal,
    body: [
      'Total entitlement, what is booked or approved, and what is left. The holiday year runs from 1 March to the end of February, and the year shown is the current one.',
      'Remaining turns red if it goes below zero, which is the sign that a request needs cancelling rather than another making.',
    ],
  },
  {
    title: 'Days that behave differently',
    target: '[data-tour="leave-extra-allowances"]',
    before: closeRequestModal,
    body: [
      'Days carried over from last year, a wellbeing day and TOIL each get a box of their own when you have any. They are kept apart from the total because they do not behave like ordinary annual leave: none of them carries into next year, and carried-over days have a date they must be taken by.',
      'Days bought with benefit points sit inside your total entitlement and appear in the breakdown below, and are booked like any other annual leave.',
      'An amber box means something is about to expire. Book it before it goes.',
    ],
  },
  {
    title: 'Days you do not need to book',
    target: '[data-tour="leave-days-off"]',
    before: closeRequestModal,
    body: [
      'Bank holidays and company closures still to come this year. They cost nobody any allowance, and a request that spans one does not count it as a day taken.',
      'If you work part time, your allowance already includes the bank holidays and they are booked against it for you at the start of each holiday year, so you never need to book them yourself. Once HR has recorded the days you work, only the bank holidays on those days are booked, a whole day each.',
    ],
  },
  {
    title: 'The breakdown by type',
    target: '[data-tour="leave-breakdown"]',
    before: closeRequestModal,
    body: [
      'Each kind of leave you hold, with its allowance, what is booked and what remains. This is where the figures in the boxes above come from. Sick leave and other leave are only counted here: they have no allowance, and never come off your holiday.',
    ],
  },
  {
    title: 'Requesting leave',
    target: '[data-tour="leave-request"]',
    before: closeRequestModal,
    body: [
      'Request Leave opens the form. Your request goes to your line manager for approval, and you will be emailed when it is decided. The next three steps walk through the form.',
    ],
  },
  {
    title: 'Which kind of leave',
    target: '[data-tour="leave-request-type"]',
    before: openRequestModal,
    body: [
      'Annual leave is the usual answer. The other kinds are here for when they apply: carried-over days, benefits leave, a wellbeing day, sick leave, company training, TOIL when you have some to take, or other leave such as jury service or compassionate leave.',
      'Picking one of the special kinds shows a line explaining how it behaves, so you are not surprised on submit.',
    ],
  },
  {
    title: 'The dates, and half days',
    target: '[data-tour="leave-request-dates"]',
    before: openRequestModal,
    body: [
      'Pick a start and an end. A single day offers all day, morning only or afternoon only, and a range can start at lunchtime or finish at it, so "off from Wednesday lunchtime" is sayable rather than rounded up to a whole day.',
      'A wellbeing day is one standalone day, so the form asks for a single date for it.',
    ],
  },
  {
    title: 'The days are worked out for you',
    target: '[data-tour="leave-request-days"]',
    before: openRequestModal,
    body: [
      'Number of Days is calculated from the dates and the half-day choices, skipping weekends and the bank holidays already in your diary. If you work part time and HR has recorded your working days, only those days count, a whole day each. It is not editable: change the dates or the half-day choices and the figure follows.',
      'The reason is optional and goes to whoever approves it. Submit Request sends it on its way.',
    ],
  },
  {
    title: 'Where your requests end up',
    target: '[data-tour="leave-requests"]',
    before: closeRequestModal,
    body: [
      'Every request this holiday year, with its status: pending, approved, rejected or cancelled. A rejection carries the reason your manager gave.',
      'A request that has not been decided yet, or approved leave that has not started, can be cancelled from the row.',
      'Approved leave that has not started can also be changed: new dates, half days or type. The change goes back to your manager, and your leave stays booked as it was until they decide, so a no costs you nothing. Until then the row shows Change pending, and you can withdraw it.',
    ],
  },
  {
    title: 'That\'s this page',
    before: closeRequestModal,
    body: [
      'Check the boxes at the top before you book, and use the table at the bottom to follow a request through. The "?" beside the page title replays this tour any time.',
    ],
  },
];
